// react
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';

// @material-ui/icons
import Announcement from "@material-ui/icons/Announcement";
import AttachFile from "@material-ui/icons/AttachFile";

// @material-ui components
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";

// core components
import GridContainer from "beaktShared/components/Grid/GridContainer.js";
import GridItem from "beaktShared/components/Grid/GridItem.js";
import Button from "beaktShared/components/CustomButtons/Button.js";
import Card from "beaktShared/components/Card/Card.js";
import CardBody from "beaktShared/components/Card/CardBody.js";
import CardFooter from "beaktShared/components/Card/CardFooter.js";
import CardIcon from "beaktShared/components/Card/CardIcon.js";
import CardHeader from "beaktShared/components/Card/CardHeader.js";
import Snackbar from "beaktShared/components/Snackbar/Snackbar.js";
import MuralDetalhe from "./MuralDetalhe";
import TipoComunicadoBadge from "beaktShared/components/Comunicado/TipoComunicadoBadge";

// services
import * as MuralService from "./MuralService";
import { formateDate, formateDateandTime } from "beaktShared/helpers/masks";
import { SessionContext } from "beaktShared/services/session";

// styles
import { makeStyles } from "@material-ui/core/styles";
import styles from "assets/jss/material-dashboard-pro-react/views/crudeStyle.js";
import { successColor, warningColor } from "assets/jss/material-dashboard-pro-react.js";

const useStyles = makeStyles({
    ...styles,
    filtros: {
        marginBottom: "16px",
        "& > *": {
            margin: "0 8px 8px 0"
        }
    },
    // O GridItem estica na linha e o Card preenche o espaço descontando as próprias margens,
    // evitando que height: 100% + margin ultrapasse o card principal
    cardItem: {
        display: "flex"
    },
    card: {
        display: "flex",
        flexDirection: "column"
    },
    cardPendente: {
        borderLeft: "4px solid " + warningColor[0]
    },
    cardLido: {
        borderLeft: "4px solid " + successColor[0]
    },
    cardBody: {
        flex: 1
    },
    titulo: {
        fontSize: "16px",
        fontWeight: 500,
        margin: "10px 0 8px"
    },
    detalhe: {
        color: "#777",
        fontSize: "13px",
        margin: "2px 0"
    },
    statusPendente: {
        color: warningColor[0],
        fontWeight: 500,
        fontSize: "13px"
    },
    statusLido: {
        color: successColor[0],
        fontWeight: 500,
        fontSize: "13px"
    },
    textCenter: {
        textAlign: "center"
    }
});

const FILTRO_TODOS = "todos";
const FILTRO_PENDENTES = "pendentes";
const FILTRO_LIDOS = "lidos";

export default function MuralList() {
    const { t } = useTranslation();
    const classes = useStyles();
    const session = useContext(SessionContext);

    const [notification, setNotification] = useState({ show: false, color: 'info', message: '' });
    const showNotification = (message, color) => {
        setNotification({ show: true, color: color, message: message });
        setTimeout(() => setNotification({ show: false }), 6000);
    };

    const [list, setList] = useState([]);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [isErrorList, setIsErrorList] = useState(false);
    const [filtro, setFiltro] = useState(FILTRO_TODOS);
    const [selecionado, setSelecionado] = useState(null);

    const loadList = () => {
        setIsLoadingList(true);
        setIsErrorList(false);
        MuralService.listarMural(session.estabelecimentoId).then(result => {
            setIsLoadingList(false);
            if (!result.ok) {
                setIsErrorList(true);
                setList([]);
                if (result.message)
                    showNotification(result.message, 'danger');
                return;
            }
            setList(result.data || []);
        });
    };

    useEffect(() => {
        loadList();
    // eslint-disable-next-line
    }, []);

    // Lista e detalhe compartilham o mesmo container de rolagem: ao alternar entre eles, volta ao topo.
    // Depende só do id para não rolar quando o aceite atualiza o comunicado aberto
    const selecionadoId = selecionado ? selecionado.comunicadoId : null;
    useEffect(() => {
        const container = document.querySelector('#divscroll');
        if (container)
            container.scrollTop = 0;
    }, [selecionadoId]);

    // O aceite atualiza o item localmente: o detalhe passa a exibir o registro e o card fica como lido
    const handleAceito = (aceite) => {
        const atualizar = (c) => ({
            ...c,
            aceito: true,
            comunicadoAceiteId: aceite.comunicadoAceiteId,
            dataHoraAceite: aceite.dataHora
        });
        setList(atual => atual.map(c => c.comunicadoId === aceite.comunicadoId ? atualizar(c) : c));
        setSelecionado(atual => atual && atual.comunicadoId === aceite.comunicadoId ? atualizar(atual) : atual);
        showNotification(t('notice_board.acceptance_registered'), 'success');
    };

    const totalPendentes = list.filter(c => !c.aceito).length;
    const totalLidos = list.length - totalPendentes;

    const listaFiltrada = list.filter(c => {
        if (filtro === FILTRO_PENDENTES) return !c.aceito;
        if (filtro === FILTRO_LIDOS) return c.aceito;
        return true;
    });

    const renderChipFiltro = (valor, label, total) => (
        <Chip
            clickable
            color={filtro === valor ? "primary" : "default"}
            variant={filtro === valor ? "default" : "outlined"}
            label={label + " (" + total + ")"}
            onClick={() => setFiltro(valor)}
        />
    );

    const renderCard = (comunicado) => (
        <GridItem xs={12} sm={6} md={4} key={comunicado.comunicadoId} className={classes.cardItem}>
            <Card className={classes.card + " " + (comunicado.aceito ? classes.cardLido : classes.cardPendente)}>
                <CardBody className={classes.cardBody}>
                    <TipoComunicadoBadge tipo={comunicado.tipo} />
                    <div className={classes.titulo}>{comunicado.titulo}</div>
                    <p className={classes.detalhe}>{t('notice_board.published')}: {formateDate(comunicado.dataCriacao)}</p>
                    {comunicado.dataLimiteAceite &&
                        <p className={classes.detalhe}>{t('notice_board.deadline')}: {formateDate(comunicado.dataLimiteAceite)}</p>}
                    {comunicado.anexos && comunicado.anexos.length > 0 &&
                        <p className={classes.detalhe}><AttachFile style={{ fontSize: "14px", verticalAlign: "middle" }} /> {comunicado.anexos.length}</p>}
                    <p>
                        {comunicado.aceito
                            ? <span className={classes.statusLido}>{t('notice_board.read_on', { date: formateDateandTime(comunicado.dataHoraAceite) })}</span>
                            : <span className={classes.statusPendente}>{t('notice_board.pending_acceptance')}</span>}
                    </p>
                </CardBody>
                <CardFooter>
                    <Button color={comunicado.aceito ? "info" : "primary"} size="sm" round onClick={() => setSelecionado(comunicado)}>
                        {comunicado.aceito ? t('notice_board.view_notice') : t('notice_board.read_and_confirm')}
                    </Button>
                </CardFooter>
            </Card>
        </GridItem>
    );

    const renderConteudo = () => {
        if (isLoadingList) {
            return (
                <div className={classes.textCenter}>
                    <CircularProgress />
                    <p>{t('label.loading')}...</p>
                </div>
            );
        }

        if (isErrorList) {
            return (
                <div className={classes.textCenter}>
                    <p>{t('notice_board.load_error')}</p>
                    <Button color="primary" onClick={loadList}>{t('notice_board.try_again')}</Button>
                </div>
            );
        }

        if (listaFiltrada.length === 0) {
            return (
                <div className={classes.textCenter}>
                    <p>{filtro === FILTRO_PENDENTES ? t('notice_board.no_pending_notices') : t('notice_board.no_notices')}</p>
                </div>
            );
        }

        return (
            <GridContainer>
                {listaFiltrada.map(renderCard)}
            </GridContainer>
        );
    };

    if (selecionado)
        return <MuralDetalhe comunicado={selecionado} onAceito={handleAceito} handleView={() => setSelecionado(null)} />;

    return (
        <div>
            {notification.show &&
                <Snackbar
                    place="tr"
                    color={notification.color}
                    message={notification.message}
                    open={notification.show}
                    closeNotification={() => setNotification({ show: false })}
                    close
                />}

            <GridContainer>
                <GridItem xs={12}>
                    <Card>
                        <CardHeader color="primary" icon>
                            <CardIcon color="primary">
                                <Announcement />
                            </CardIcon>
                            <h4 className={classes.cardIconTitle}>{t('notice_board.title')}</h4>
                        </CardHeader>
                        <CardBody>
                            <div className={classes.filtros}>
                                {renderChipFiltro(FILTRO_TODOS, t('label.all'), list.length)}
                                {renderChipFiltro(FILTRO_PENDENTES, t('notice_board.filter_pending'), totalPendentes)}
                                {renderChipFiltro(FILTRO_LIDOS, t('notice_board.filter_read'), totalLidos)}
                            </div>
                            {renderConteudo()}
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>
        </div>
    );
}
